import { LoadingOutlined, SearchOutlined } from "@ant-design/icons"
import { Divider, Flex, type SelectProps, Typography } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React, { useEffect, useState } from "react"

import SelectComponent from "../../../../../components/Select"
import useDebounce from "../../../../../hooks/use-debounce"
import { showErrorNotifications } from "../../../../../utils/handlers/handle-error"
import { searchProjects } from "../../../api/services.ts"

const PAGE_SIZE = 30

export const CalendarAddProjectsSelect = React.forwardRef<
  HTMLSelectElement,
  SelectProps
>(({ onChange, ...props }, ref) => {
  const [search, setSearch] = useState<string>("")
  const [items, setItems] = useState<BaseOptionType[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [total, setTotal] = useState<number>(0)

  const debounced = useDebounce(search, 500)

  const onSearchDependenciesHandler = async () => {
    try {
      setLoading(true)

      const res = await searchProjects({
        page: 1,
        page_size: PAGE_SIZE,
        ...(debounced ? { q: debounced } : {})
      })

      if (!res?.ok) throw new Error(res?.data?.details?.message)

      const projects = res?.data?.results || []
      const total: number = res?.data?.count || 0

      setTotal(total)

      setItems(
        projects?.map((project) => ({
          value: project?.id,
          label: project?.lead_name || "-",
          suggestedAddress: project?.suggested_meeting_address || ""
        }))
      )
    } catch (e) {
      showErrorNotifications(e)
    } finally {
      setLoading(false)
    }
  }

  const onChangeHandler: SelectProps["onChange"] = (value: string, option) => {
    // The option carries `suggestedAddress`; the form needs it to offer the
    // meeting location, so it has to travel with the value.
    onChange?.(value, option)

    // if it is null, it means that user cleared the selection
    if (!value) {
      setSearch("")
    }
  }

  useEffect(() => {
    void onSearchDependenciesHandler()
  }, [debounced])

  return (
    <SelectComponent
      ref={ref}
      showSearch
      onSearch={setSearch}
      onChange={onChangeHandler}
      onOpenChange={(v) => v && onSearchDependenciesHandler()}
      filterOption={false}
      loading={loading}
      size="large"
      optionLabelProp="label"
      placeholder="Search by name"
      options={items}
      suffixIcon={loading ? <LoadingOutlined /> : <SearchOutlined />}
      maxTagCount={"responsive"}
      notFoundContent={loading ? "Searching..." : "No projects found"}
      dropdownRender={(menu) => (
        <>
          {menu}

          {!!items?.length && (
            <>
              <Divider size={"small"} />

              <Flex className={"p-2"} gap={"small"} justify={"center"}>
                {loading ? (
                  <>
                    <Typography.Text>Searching...</Typography.Text>
                    <LoadingOutlined />
                  </>
                ) : (
                  <Typography.Text>
                    {total} project{total > 1 ? "s" : ""} found — start typing
                    to filter them
                  </Typography.Text>
                )}
              </Flex>
            </>
          )}
        </>
      )}
      {...props}
    />
  )
})

CalendarAddProjectsSelect.displayName = "CalendarAddProjectsSelect"
